import { useEffect, useState, useContext, useRef } from "react";
import { createBrowserRouter, useNavigate, useParams, useSearchParams } from "react-router";
import { renderToStaticMarkup } from 'react-dom/server';

import { AppContext } from '../../context.js';
import { Minus, CheckLarge, Print } from "../../files/icons/index.js";
import Loading from "../components/loading.js";
import axios from "axios";
import Helmet from "../components/helmet.js";

import { DatetimeToDate, DateToTimestamp, DateToFormat, calculateAge } from "../../utils/datetime.js";

function Export() {
  const context = useContext(AppContext);

  // Get URL search parameters to determine export scope
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectID, setProjectID] = useState(searchParams.get("projectID"));
  const [assessmentID, setAssessmentID] = useState(searchParams.get("assessmentID"));
  const [patientID, setPatientID] = useState(searchParams.get("patientID"));
  
  // Determine export scope based on available parameters
  const scope = assessmentID ? 'assessment' : patientID ? 'patient' : 'project';

  const [loading, setLoading] = useState(true);

  const [project, setProject] = useState(null);
  const [plan, setPlan] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [assessment, setAssessment] = useState(null);
  const [patient, setPatient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [settings, setSettings] = useState(null);

  // General export settings
  const [exportGeneralPatientData, setExportGeneralPatientData] = useState(true);

  // Project export settings
  const [exportDemographic, setExportDemographic] = useState([]);
  const [exportCarePlan, setExportCarePlan] = useState([]);
  const [exportTasks, setExportTasks] = useState(true);
  const [exportAppointments, setExportAppointments] = useState(true);
  const [exportAssessments, setExportAssessments] = useState(true);
  const [exportClinicalNotes, setExportClinicalNotes] = useState(true);
  
  // Assessment export settings
  const [exportAssessmentScores, setExportAssessmentScores] = useState(true);
  const [exportAssessmentDetails, setExportAssessmentDetails] = useState(true);
  const [exportAssessmentResponses, setExportAssessmentResponses] = useState(true);
  
  // Patient export settings
  const [exportPatientProfile, setExportPatientProfile] = useState(true);
  const [exportPatientHistory, setExportPatientHistory] = useState(true);
  const [exportPatientProjects, setExportPatientProjects] = useState(true);

  useEffect(() => {
    switch (scope) {
      case 'project':
        getForProject();
        break;
      case 'assessment':
        getForAssessment();
        break;
      case 'patient':
        getForPatient();
        break;
      default: 
        break;
    }
  }, [projectID]);

  function getForProject() {
    let u = context.addSpiner();
    axios.all([
      axios.post('/api/admin/projects/get', {
        id: projectID
      }),
      axios.get('/api/admin/settings', {
      }),
    ]).then(axios.spread((project, settings) => {
      setProject(project.data);
      setSettings(settings.data);
     
      // Get appoitments, assessments, patient, accounts, plan
      axios.all([
        axios.post('/api/admin/tasks', {
          filter: { projectID: [project.data.projectID] }
        }),
        axios.post('/api/admin/appointments', {
          filter: { patientID: [project.data.patientID] }
        }),
        axios.post('/api/admin/assessments', {
          filter: { patientID: [project.data.patientID] }
        }),
        axios.post('/api/admin/patients', {
          filter: {
            id: [project.data.patientID],
          }
        }),
        axios.post('/api/admin/accounts', {
          filter: {
            userID: [project.data.patientID],
          },
        }),
        // axios.post('/api/admin/plans/get', {
        //   patientID: project.data.patientID,
        // }),
      ]).then(axios.spread((tasks, appointments, assessments, patient, accounts) => {
        setTasks(tasks.data.tasks);
        setAppointments(appointments.data.appointments);
        setAssessments(assessments.data.assessments);
        setPatient(patient.data.patients[0]);
        setAccounts(accounts.data.accounts);
        setPlan(null);

        // Set default export settings
        setExportDemographic([patient.data.patients[0].pages[0].id]);
        setExportCarePlan([]);

        setLoading(false); // Set loading to false after accounts are loaded
      })).catch((e) => {
        // context.addError(e);
        // context.removeSpiner(u);
      }).finally(() => {
        //context.removeSpiner(u);
      });

      //setLoading(false);
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function getForAssessment() {
    let u = context.addSpiner();
    axios.all([
      axios.post('/api/admin/assessments', {
        filter: {
          id: [assessmentID],
        }
      }),
      axios.get('/api/admin/settings', {
      }),
    ]).then(axios.spread((assessments, settings) => {
      setAssessment(assessments.data.assessments[0]);
      setSettings(settings.data);

      // Get patient and accounts
      axios.all([
        axios.post('/api/admin/patients', {
          filter: {
            id: [assessments.data.assessments[0].patientID],
          }
        }),
      ]).then(axios.spread((patient) => {
        setPatient(patient.data.patients[0]);

        setLoading(false);
      })).catch((e) => {
        context.addError(e);
        context.removeSpiner(u);
      }).finally(() => {
        context.removeSpiner(u);
      });
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function getForPatient() {
  }

  // Menu
  function renderExportTitle() {
    switch (scope) {
      case 'assessment':
        return (
          <div className="card-header sm">
            <div className="typo-strong-300">Assessment export</div>
          </div>
        );
      case 'patient':
        return (
          <div className="card-header sm">
            <div className="typo-strong-300">Patient export</div>
          </div>
        );
      case 'project':
        return (
          <div className="card-header sm">
            <div className="typo-strong-300">Project export</div>
          </div>
        );
      default:
        return null;
    }
  }

  function renderMenu() {
    switch (scope) {
      case 'assessment':
        return renderAssessmentMenu();
      case 'patient':
        return renderPatientMenu();
      case 'project':
        return renderProjectMenu();
      default:
        return null;
    }
  }

  function renderAssessmentMenu() {
    return (
      <div className="card-header sm">
        <div className="typo-strong-200">Patient</div>
        <div className="item d-flex justify-content-start align-items-center" onClick={() => setExportGeneralPatientData(!exportGeneralPatientData)}>
          {exportGeneralPatientData ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
          <div className="typo-normal-150">Patient data</div>
        </div>

        <div className="typo-strong-200">Assessment</div>
        <div className="item d-flex justify-content-start align-items-center" onClick={() => setExportAssessmentScores(!exportAssessmentScores)}>
          {exportAssessmentScores ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
          <div className="typo-normal-150">Scores</div>
        </div>
      </div>
    );
  }

  function renderPatientMenu() {
    return (
      <></>
    );
  }

  function renderProjectMenu() {
    return (
      <div className="card-header sm">

        <div className="typo-strong-200">Patient</div>
        <div className="item d-flex justify-content-start align-items-center" onClick={() => setExportGeneralPatientData(!exportGeneralPatientData)}>
          {exportGeneralPatientData ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
          <div className="typo-normal-150">Patient data</div>
        </div>

        {/* <div className="typo-strong-200">Care Plan</div>
        {plan?.sections && plan.sections.map((section) => (
          <div key={section.id} className="item d-flex justify-content-start align-items-center" onClick={() => carePlanSectionChange(section.id)}>
            {exportCarePlan.includes(section.id) ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
            <div className="typo-normal-150">{section.title}</div>
          </div>
        ))} */}

        <div className="typo-strong-200 mt-2">Completed Activities</div>
        <div className="item d-flex justify-content-start align-items-center" onClick={() => setExportTasks(!exportTasks)}>
          {exportTasks ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
          <div className="typo-normal-150">Tasks</div>
        </div>
        <div className="item d-flex justify-content-start align-items-center" onClick={() => setExportAppointments(!exportAppointments)}>
          {exportAppointments ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
          <div className="typo-normal-150">Appointments</div>
        </div>
        <div className="item d-flex justify-content-start align-items-center" onClick={() => setExportAssessments(!exportAssessments)}>
          {exportAssessments ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
          <div className="typo-normal-150">Assessments</div>
        </div>

        {/* <div className="typo-strong-200 mt-2">Clinical Notes</div>
        <div className="item d-flex justify-content-start align-items-center" onClick={() => setExportClinicalNotes(!exportClinicalNotes)}>
          {exportClinicalNotes ? <CheckLarge height={16} width={16} color={'green'} /> : <Minus height={16} width={16} color={'lightgrey'} />}
          <div className="typo-normal-150">All notes</div>
        </div> */}

        {/* <div className="typo-strong-200">Account Atilization</div>
        <div className="item d-flex justify-content-start align-items-center">
          <CheckLarge height={16} width={16} color={'green'} />
          <div className="typo-normal-150">Personal information</div>
        </div>
        <div className="item d-flex justify-content-start align-items-center">
          <Minus height={16} width={16} color={'lightgrey'} />
          <div className="typo-normal-150">Tasks</div>
        </div> */}

        {/* <div className="typo-strong-200">Clinical Notes</div>
        <div className="item d-flex justify-content-start align-items-center">
          <CheckLarge height={16} width={16} color={'green'} />
          <div className="typo-normal-150">Personal information</div>
        </div>
        <div className="item d-flex justify-content-start align-items-center">
          <Minus height={16} width={16} color={'lightgrey'} />
          <div className="typo-normal-150">Tasks</div>
        </div> */}
      </div>
    );
  }

  function patientProfileChange(pageID) {
    let newExportDemographic = [...exportDemographic];
    let index = newExportDemographic.findIndex((i) => i === pageID);
    if (index !== -1) {
      // Remove the page from the export
      newExportDemographic.splice(index, 1);
    } else {
      // Add the page to the export
      newExportDemographic.push(pageID);
    }
    setExportDemographic(newExportDemographic);
  }

  function carePlanSectionChange(sectionID) {
    let newExportCarePlan = [...exportCarePlan];
    let index = newExportCarePlan.findIndex((i) => i === sectionID);
    if (index !== -1) {
      // Remove the section from the export
      newExportCarePlan.splice(index, 1);
    } else {
      // Add the section to the export
      newExportCarePlan.push(sectionID);
    }
    setExportCarePlan(newExportCarePlan);
  }

  // Helper function to get field value from patient profile
  function getPatientFieldValue(code) {
    if (!patient || !patient.fields) {
      return null;
    }
    
    const field = patient.fields.find(field => field.code === code);
    if (!field || !field.answers || field.answers.length === 0) {
      return null;
    }
    
    return field.answers[0];
  }

  // Pages
  function renderPages() {
    let content = [];

    // Add company header
    content.push({
      content: renderDocumentHeader(),
      height: 56,
    });

    content.push({
      content: renderSpace(30),
      height: 30,
    });

    // General blocks
    if (exportGeneralPatientData) {

      // Patient name
      content.push({
        content: renderH1(patient.firstName + " " + patient.lastName),
        height: 36,
      });

      content.push({
        content: renderSpace(5),
        height: 5,
      });
      
      content.push({
        content: renderHR(),
        height: 2,
      });

      content.push({
        content: renderSpace(5),
        height: 5,
      });

      // Patient DOB, Medical Record Number (MRN) and gender
      content.push({
        content: <div className="d-flex justify-content-start align-items-top">
          <div className="w-50">
            <div className="typo-strong-150">Medical Record Number (MRN):</div>
            <div className="typo-normal-175">{getPatientFieldValue("medicalRecordNumber") || "n/a"}</div>
          </div>
          <div className="w-25">
            <div className="typo-strong-150">Date of Birth:</div>
            <div className="typo-normal-175">{getPatientFieldValue("dateOfBirth") ? DateToFormat(DatetimeToDate(getPatientFieldValue("dateOfBirth")), context.dateFormat) : "n/a"}</div>
          </div>
          <div className="w-25">
            <div className="typo-strong-150">Gender:</div>
            <div className="typo-normal-175">{getPatientFieldValue("gender") || "n/a"}</div>
          </div>
        </div>,
        height: 44,
      });

      content.push({
        content: renderSpace(30),
        height: 30,
      });
    }

    switch (scope) {
      case 'assessment':

        // Render title
        content.push({
          content: renderH1(assessment.title),
          height: 36,
        });

        content.push({
          content: renderSpace(5),
          height: 5,
        });

        content.push({
          content: renderHR(),
          height: 2,
        });

        content.push({
          content: renderSpace(5),
          height: 5,
        });

        // Render assessment details
        content.push({
          content: <div className="d-flex justify-content-between align-items-top">
            <div className="w-50">
              <div className="typo-strong-150">Assigned to:</div>
              <div className="typo-normal-175">{assessment.userName ? assessment.userName : "Unassigned"}</div>
            </div>
            <div className="w-25">
              <div className="typo-strong-150">Created at:</div>
              <div className="typo-normal-175">{assessment.createdAt ? DateToFormat(DatetimeToDate(assessment.createdAt), context.dateFormat) : ""}</div>
            </div>
            <div className="w-25">
              <div className="typo-strong-150">Closed at:</div>
              <div className="typo-normal-175">{assessment.closedAt ? DateToFormat(DatetimeToDate(assessment.closedAt), context.dateFormat) : ""}</div>
            </div>
          </div>,
          height: 44,
        });

        content.push({
          content: renderSpace(5),
          height: 5,
        });

        content.push({
          content: renderHR(),
          height: 2,
        });

        content.push({
          content: renderSpace(5),
          height: 5,
        });

        // Render scores
        if (exportAssessmentScores) {
          if (assessment.scores && assessment.scores.length > 0) {
            let scores = assessment.scores.length;
            if (scores > 4) {
              scores = 4;
            }

            let scoresContent = (
              <div className="d-flex justify-content-start align-items-top">
                {assessment.scores.slice(0, scores).map((score, index) => (
                  <div key={index} className="w-25">
                    <div className="typo-strong-150">{score.title}</div>
                    <div className="typo-normal-175">{score.score}</div>
                  </div>
                ))}
              </div>
            );

            content.push({
              content: scoresContent,
              height: 44,
            });

            content.push({
              content: renderHR(),
              height: 2,
            });

            content.push({
              content: renderSpace(10),
              height: 10,
            });
          }
        }

        // Render questions
        assessment.questions.forEach((question, index) => {
          content.push(calculateHeight(
            <div className="typo-strong-175">{question.title}</div>
          ));

          if (question.answerValues && question.answerValues.length > 0) {
            let answer;
            switch (question.answerType) {
              case 'text':
                answer = question.answerValues[0];
                break;
              case 'date':
                answer = DateToFormat(DatetimeToDate(question.answerValues[0]), context.dateFormat);
                break;
              case 'options':
              case 'optionsColumn':
                answer = question.answerOptions[question.answerValues[0]] || "n/a";
                break;
              case 'checkboxes':
              case 'checkboxesColumn':
                // Map indices to option texts and join with commas
                answer = question.answerValues
                  .map(index => question.answerOptions[parseInt(index)] || "n/a")
                  .join(", ");
                break;
              default:
                answer = question.answerValues[0];
            }

            content.push(calculateHeight(
              <div className="typo-normal-175">{answer}</div>
            ));
          } else {
            content.push(calculateHeight(
              <div className="typo-normal-175">n/a</div>
            ));
          }

          content.push({
            content: renderSpace(5),
            height: 5,
          });
        });

        break;
      case 'patient':
        break;
      case 'project':
        content.push({
          content: <div className="d-flex justify-content-between align-items-top">
            <div className="w-50">
              <div className="typo-strong-150">Assigned to:</div>
              <div className="typo-normal-175">{project.userName ? project.userName : "Unassigned"}</div>
            </div>
            <div className="w-25">
              <div className="typo-strong-150">Created at:</div>
              <div className="typo-normal-175">{project.createdAt ? DateToFormat(DatetimeToDate(project.createdAt), context.dateFormat) : ""}</div>
            </div>
            <div className="w-25">
              <div className="typo-strong-150">Closed at:</div>
              <div className="typo-normal-175">{project.closedAt ? DateToFormat(DatetimeToDate(project.closedAt), context.dateFormat) : ""}</div>
            </div>
          </div>,
          height: 44,
        });

        content.push({
          content: renderSpace(30),
          height: 30,
        });

        // Add patient demographic

        // Add care plan
        if (exportCarePlan.length > 0 && plan?.sections) {
          content.push({
            content: renderH1("Care Plan"),
            height: 36,
          });

          content.push({
            content: renderHR(),
            height: 2,
          });

          plan.sections.forEach((section) => {
            // Only include sections that are selected for export
            if (!exportCarePlan.includes(section.id)) {
              return;
            }

            content.push({
              content: <div className="typo-strong-200">{section.title}</div>,
              height: 20,
            });

            if ((section.description === null || section.description === undefined || section.description === '') && (!section.actions || section.actions.length === 0)) {
              content.push({
                  content: renderSpace(20),
                  height: 20,
                });
            } else {
              content.push(calculateHeight(
                <div className="typo-normal-150" dangerouslySetInnerHTML={{__html: section.description}} />
              ));

              section.actions.forEach((action) => {
                content.push({
                  content: (
                    <div className="d-flex align-items-center">
                      <span style={{marginRight: '8px'}}>{action.status ? '☑' : '☐'}</span>
                      <span className="typo-normal-150">{action.title}</span>
                    </div>
                  ),
                  height: 22,
                });
              });
            }
          });

          content.push({
            content: renderSpace(20),
            height: 20,
          });
        }

        // Add completed activities
        if (exportTasks || exportAppointments || exportAssessments) {
          content.push({
            content: renderH1("Completed Activities"),
            height: 36,
          });

          content.push({
            content: renderHR(),
            height: 2,
          });

          // Add tasks
          if (exportTasks) {
            let completedTasks = tasks.filter(task => task.statusType === 'COMPLETED');
            if (completedTasks.length > 0) {
              content.push({
                content: <div className="typo-strong-150">Tasks</div>,
                height: 28,
              });

              content.push({
                content: renderActivityRow("Task", "Completed By", "Completion Date"),
                height: 28,
              });

              completedTasks.forEach((task, index) => {
                content.push({
                  content: renderActivityRow(task.title, task.closedByName, task.closedAt ? DateToFormat(DatetimeToDate(task.closedAt), context.dateFormat) : 'N/A'),
                  height: 28,
                });
              });
            }
          }

          // Add appointments
          if (exportAppointments) {
            let completedAppointments = appointments.filter(appointment => appointment.statusType === 'COMPLETED' && appointment.closedAt > project.createdAt);
            if (completedAppointments.length > 0) {
              content.push({
                content: <div className="typo-strong-150">Appointments</div>,
                height: 28,
              });

              content.push({
                content: renderActivityRow("Appointment", "Completed By", "Completion Date"),
                height: 28,
              });

              completedAppointments.forEach((appointment, index) => {
                content.push({
                  content: renderActivityRow(appointment.title, appointment.closedByName, appointment.closedAt ? DateToFormat(DatetimeToDate(appointment.closedAt), context.dateFormat) : 'N/A'),
                  height: 28,
                });
              });
            }
          }

          // Add assessments
          if (exportAssessments) {
            let completedAssessments = assessments.filter(assessment => assessment.statusType === 'COMPLETED' && assessment.closedAt > project.createdAt);
            if (completedAssessments.length > 0) {
              content.push({
                content: <div className="typo-strong-150">Assessments</div>,
                height: 28,
              });

              content.push({
                content: renderActivityRow("Assessment", "Completed By", "Completion Date"),
                height: 28,
              });

              completedAssessments.forEach((assessment, index) => {
                content.push({
                  content: renderActivityRow(assessment.title, assessment.closedByName, assessment.closedAt ? DateToFormat(DatetimeToDate(assessment.closedAt), context.dateFormat) : 'N/A'),
                  height: 28,
                });
              });
            }
          }
        } 

        // Add clinical notes
        break;
      default:
        break;
    }

    const limit = 900;
    let pages = [];
    let page = [];
    let pageHeight = 0;

    for (let i = 0; i < content.length; i++) {
      const item = content[i];
      const itemHeight = item.height;

      if (pageHeight + itemHeight > limit) {
        // If the current page is full, push it to pages
        pages.push(
          <div className="page" key={pages.length}>
            <div className="subpage">
              {page}
            </div>
          </div>
        );
        page = [];
        pageHeight = 0;
      }

      // Add the item to the current page
      page.push(item.content);
      pageHeight += itemHeight;
    }

    // If there is any remaining content in the current page, push it to pages
    if (page.length > 0) {
      pages.push(
        <div className="page" key={pages.length}>
          <div className="subpage">
            {page}
          </div>
        </div>
      );
    }

    return pages;
  }

  // Render project

  // Shared rendering functions
  function renderDocumentHeader() {
    return (
      <div style={{display: "flex", justifyContent: "space-between"}}>
        <div style={{width: "50%"}}>
          <div className="typo-strong-300">{settings.title}</div>
          <div className="typo-normal-150">{settings.description}</div>
          {/* <span>8416 Old McGregor Rd, Woodway, TX  76712</span><br/> */}
          {/* <a href="office@vespermedicalcare.com">office@vespermedicalcare.com</a><br/> */}
          {/* <a href="tel:254-307-3997">254-307-3997</a> */}
        </div>
        <div style={{width: "50%", textAlign: "right"}}>
          <img src={"/api/admin/settings/logo?token="+context.token} style={{height: 50}} />
        </div>
      </div>
    );
  }

  function renderH1(text) {
    return (
      <div className="typo-strong-300">{text}</div>
    );
  }

  function renderH2(text) {
    return (
      <div className="typo-strong-150">{text}</div>
    );
  }

  function renderHR() {
    return (
      <hr />
    );
  }

  function renderActivityRow(col1, col2, col3) {
    return (
      <div style={{display: "flex", justifyContent: "space-between"}}>
        <div style={{width: "50%"}}>
          <div className="typo-normal-150">{col1}</div>
        </div>
        <div style={{width: "25%"}}>
          <div className="typo-normal-150">{col2}</div>
        </div>
        <div style={{width: "25%"}}>
          <div className="typo-normal-150">{col3}</div>
        </div>
      </div>
    );
  }

  function renderSpace(height) {
    return (
      <div style={{height: height}}></div>
    );
  }

  function calculateHeight(content) {
    let height = 0;
    let width = 0;
  
    const page = document.createElement("div");
    page.className = "page";

    const subpage = document.createElement("div");
    subpage.className = "subpage";
    
    // Reset height to auto to get actual content height
    subpage.style.height = "auto";

    // Convert React element to HTML string if it's a React element
    if (typeof content === 'object' && content.type) {
      subpage.innerHTML = renderToStaticMarkup(content);
    } else {
      subpage.innerHTML = content;
    }
    
    page.appendChild(subpage);

    document.body.appendChild(page);
    height = subpage.offsetHeight;
    width = subpage.offsetWidth;
    document.body.removeChild(page);

    console.log("calculateHeight");
    console.log(content);
    console.log(height);
    console.log(width);

    return {
      content: content,
      height: height,
    };
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet title="Export" />

      <div className="container">
        <div className="row">
          <div className="col-xs-3 book-menu">
            <div className="card">
              {renderExportTitle()}
              {renderMenu()}
              <div className="card-content sm text-end">
                <div className="item grey typo-normal-150" onClick={() => window.print()}><Print height={16} width={16} color={'grey'} /> Print</div>
              </div>
            </div>
          </div>
          <div className="col-xs-9 book">
            {renderPages()}
          </div>
        </div>
      </div>
    </>
  );
}

export default Export;
